/**
 * Auto Add Gift
 * Automatically adds a gift item to cart when total exceeds threshold
 */

class AutoAddGift {
  constructor() {
    this.enabled = theme.settings.enableAutoAddGift;
    this.threshold = parseFloat(theme.settings.autoAddThreshold) * 100; // Convert to cents
    this.variantId = theme.settings.autoAddVariantId;
    this.isProcessing = false;
    this.giftInCart = false;
    
    if (!this.enabled || !this.variantId || !this.threshold) {
      return;
    }

    this.init();
  }

  init() {
    // Listen for cart changes
    document.addEventListener('theme:cart:change', () => {
      this.checkCartTotal();
    });

    // Listen for product additions
    document.addEventListener('theme:product:added', () => {
      this.checkCartTotal();
    });

    // Check on initial load
    this.checkCartTotal();
  }

  /**
   * Check current cart total and add/remove gift if needed
   */
  async checkCartTotal() {
    if (this.isProcessing) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartTotal = cart.total_price;
      const hasGift = this.checkIfGiftInCart(cart.items);
      
      // Add gift if total exceeds threshold and gift not in cart
      if (cartTotal >= this.threshold && !hasGift) {
        await this.addGiftToCart();
      }
      // Remove gift if total is below threshold and gift is in cart
      else if (cartTotal < this.threshold && hasGift) {
        await this.removeGiftFromCart(cart.items);
      }
    } catch (error) {
      console.error('Error checking cart total:', error);
    }
  }

  /**
   * Check if gift item is already in cart
   */
  checkIfGiftInCart(items) {
    return items.some(item => item.variant_id.toString() === this.variantId.toString());
  }

  /**
   * Add gift item to cart
   */
  async addGiftToCart() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;

    try {
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', 1);
      formData.append('properties[_gift]', 'true'); // Mark as gift with property

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript',
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        this.giftInCart = true;
        
        // Refresh cart display without closing drawer
        await this.refreshCartDisplay();
      } else {
        const error = await response.json();
        console.error('Error adding gift to cart:', error);
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove gift item from cart
   */
  async removeGiftFromCart(items) {
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      // Find the gift item
      const giftItem = items.find(item => 
        item.variant_id.toString() === this.variantId.toString() &&
        item.properties && item.properties._gift === 'true'
      );

      if (!giftItem) {
        this.isProcessing = false;
        return;
      }

      // Get the line item index (1-based)
      const lineIndex = items.indexOf(giftItem) + 1;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0,
        }),
      });

      if (response.ok) {
        this.giftInCart = false;
        
        // Refresh cart display without closing drawer
        await this.refreshCartDisplay();
      } else {
        const error = await response.json();
        console.error('Error removing gift from cart:', error);
      }
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Refresh cart display without reloading drawer
   */
  async refreshCartDisplay() {
    try {
      const response = await fetch(`${theme.routes.cart_url}?section_id=api-cart-items`);
      const text = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const newContent = doc.querySelector('[data-api-content]');
      
      if (newContent) {
        // Trigger cart update event so cart-items component rebuilds
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
          bubbles: true,
        }));
      }
    } catch (error) {
      console.error('Error refreshing cart display:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.autoAddGift = new AutoAddGift();
  });
} else {
  window.autoAddGift = new AutoAddGift();
}
